import React, { useEffect, useRef, useState } from 'react';
import '../styles/Input.css';
import { Arrow, Info } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { IconButton, Tooltip } from './';

function Input(props) {
    const darkMode = props.darkMode || false;
    const label = props.label;
    const labelType = props.labelType;
    // Accessible name for inputs that deliberately render no visible <label> — the
    // calculator ledgers sit their fields inside a sentence ("[11] rooms × [$1,001]/mo")
    // where a label above each box would break the line. The surrounding words name the
    // field for a sighted reader; this names it for everyone else. Opt-in, so nothing
    // that already renders a <label> gains a competing announcement.
    const ariaLabel = props.ariaLabel;
    const required = props.required;
    const info = props.info;
    const infoText = props.infoText;
    const onMouseEnter = props.onMouseEnter;
    const onMouseLeave = props.onMouseLeave;
    const labelStyle = labelType === "default" && darkMode === true ? "body-bold colour-white" : labelType === "default" ? "body-bold colour-primary" : "label-bold colour-primary";
    const value = props.value;
    const onChange = props.onChange;
    const placeholder = props.placeholder;
    const type = props.type;
    const inputMode = props.inputMode;
    const leftUnit = props.leftUnit;
    const rightUnit = props.rightUnit;
    const unitSwitch = props.unitSwitch;
    const leftUnitSelected = props.leftUnitSelected;
    const unitSwitchFunc = props.unitSwitchFunc;
    const disabled = props.disabled;
    const multiline = props.multiline;
    const error = props.error;
    const reset = props.reset;
    const resetFunc = props.resetFunc;
    const resetPosition = props.resetPosition;
    // Optional node rendered inside the outer row, between the reset link and the
    // input itself — e.g. the co-living calculator's "3% below asking" note. DOM
    // order matters here: input-outer-wrapper-row is row-reversed so Reset lands
    // on the left, so this must sit between the input-wrapper and the reset span
    // in markup order to end up visually between them (Reset · note · input).
    const note = props.note;
    const secondaryLabel = props.secondaryLabel;
    const secondaryLabelGrey = props.secondaryLabelGrey;
    const focusMultiline = props.focusMultiline;
    const prompt = props.prompt;
    const submitPromptDisabled = props.submitPromptDisabled;
    const submitPrompt = props.submitPrompt;
    const onHeightChange = props.onHeightChange;
    const multilineRows = props.multilineRows ?? 4;
    const internalTextareaRef = useRef(null);
    const externalTextareaRef = props.textareaRef;
    const textareaRef = externalTextareaRef || internalTextareaRef;
    const externalOnKeyDown = props.onKeyDown;
    const onBlur = props.onBlur;
    const [showInfoTooltip, setShowInfoTooltip] = useState(false);
    const infoTooltipTimer = useRef(null);
    const hasInfoText = typeof infoText === 'string' && infoText.trim() !== '';
    const tooltipTitle = typeof label === 'string' ? label : null;

    const handleInfoMouseEnter = () => {
        if (hasInfoText) {
            infoTooltipTimer.current = setTimeout(() => setShowInfoTooltip(true), 500);
        }
        if (onMouseEnter) {
            onMouseEnter();
        }
    };

    const handleInfoMouseLeave = () => {
        clearTimeout(infoTooltipTimer.current);
        setShowInfoTooltip(false);
        if (onMouseLeave) {
            onMouseLeave();
        }
    };

    // Ensure text remains selected on focus
    useEffect(() => {
        if (focusMultiline && textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.select();
        }
    }, [focusMultiline, textareaRef]);


    return (
        <div className={"input-container " + (darkMode === true ? "input-dark " : "") + (error === true ? "input-error " : "") + (labelType === null || labelType === "inside" ? "" : labelType === "default" ? "gap-small" : "gap-x-small")}>
            {
                labelType === null || labelType === "inside" ?
                null
                :
                <div className={"relative-container " + (info === true ? "input-label-container " : secondaryLabelGrey !== undefined && secondaryLabelGrey !== null ? "input-secondary-label-container " : " ") + (info === false ? "" : labelType === "default" ? "gap-x-small" : "gap-x-x-small")}>
                    <label className={labelStyle}>
                        {label}{required === true && <span className="colour-error"> *</span>}{
                            secondaryLabel !== undefined && secondaryLabel !== null &&
                            <span className="body-regular colour-primary">
                                {" "}{secondaryLabel}
                            </span>
                        }
                    </label>
                    {
                        info === true &&
                        (
                            <span
                                className={"input-info-tooltip-wrapper " + (hasInfoText ? "relative-container" : "")}
                                onMouseEnter={handleInfoMouseEnter}
                                onMouseLeave={handleInfoMouseLeave}
                            >
                            <Info
                                fill={colour.gray05}
                                className="cursor-pointer"
                            />
                            {
                                hasInfoText &&
                                <Tooltip
                                    type="info"
                                    title={tooltipTitle}
                                    body={infoText}
                                    position="top"
                                    contextTooltipBg={null}
                                    exit={false}
                                    exitFunc={null}
                                    customClass={showInfoTooltip ? "fade-in" : "fade-out"}
                                />
                            }
                            </span>
                        )
                    }
                    {
                        secondaryLabelGrey !== undefined && secondaryLabelGrey !== null &&
                        <span className="body-regular colour-secondary">
                            {secondaryLabelGrey}
                        </span>
                    }
                </div>
            }
            <div className={resetPosition === "default" ? "input-outer-wrapper-row " : "input-outer-wrapper-column " + (prompt === true ? "relative-container prompt-input-wrapper" : "")}>
                <div className={"input-wrapper " + (disabled === true ? "disabled " : "")}>
                    {
                        labelType !== "inside" ?
                        null
                        :
                        <div className={"input-label-container input-label-inside-container gap-x-small" }>
                            <label className={labelStyle}>
                                {label}{required === true && <span className="colour-error"> *</span>}
                            </label>
                        </div>
                    }
                    <div className="input-inner-wrapper">
                        {
                            leftUnit === null ?
                            null
                            :
                            <span
                                className={"body-regular cursor-pointer " + (leftUnitSelected === true || unitSwitch === false ? "colour-primary " : "colour-tertiary ") + (disabled === true ? "disabled" : "colour-primary-hover")}
                                onClick={() => (unitSwitch === false || disabled === true) ? null : unitSwitchFunc(false)}
                            >
                                {leftUnit}
                            </span>
                        }
                        {
                            prompt === true && multiline === true ?
                            <textarea
                                ref={textareaRef}
                                value={value}
                                onChange={onChange}
                                className={"input body-regular no-resize colour-primary " + (disabled === true ? "disabled" : "")}
                                placeholder={placeholder}
                                onInput={(e) => {
                                    e.target.style.height = "auto";
                                    e.target.style.height = e.target.scrollHeight + "px";
                                    if (onHeightChange) {
                                        onHeightChange(e.target.scrollHeight);
                                    }
                                }}
                                rows={1}  
                                disabled={disabled}
                                onFocus={focusMultiline ? (e) => setTimeout(() => e.target.select(), 0) : null}
                                onKeyDown={(e) => {
                                    // Call external onKeyDown first (for mention handling)
                                    if (externalOnKeyDown) {
                                        externalOnKeyDown(e);
                                    }
                                    // Handle Enter for submit (only if not prevented by external handler)
                                    if (e.key === "Enter" && !e.shiftKey && !e.defaultPrevented) {
                                        if (value.trim() !== "") {
                                            e.preventDefault(); // Prevent new line
                                            submitPrompt(); // Call the submit function
                                        }
                                    }
                                }}
                            />
                            :
                            multiline === true ?
                            <textarea
                                ref={textareaRef}
                                value={value}
                                onChange={onChange}
                                className={"input body-regular colour-primary " + (disabled === true ? "disabled" : "")}
                                placeholder={placeholder}
                                rows={multilineRows}
                                disabled={disabled}
                                onFocus={focusMultiline ? (e) => setTimeout(() => e.target.select(), 0) : null}
                            />
                            :
                            <input
                                type={type}
                                inputMode={inputMode}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                aria-label={ariaLabel || undefined}
                                className={"input body-regular colour-primary " + (leftUnitSelected === true ? "text-align-left " : "text-align-right ") + (disabled === true ? "disabled" : "")}
                                placeholder={placeholder}
                                disabled={disabled}
                            />
                        }
                        {
                            rightUnit === null ?
                            null
                            :
                            <span 
                                className={"body-regular cursor-pointer " + (leftUnitSelected === false || unitSwitch === false ? "colour-primary " : "colour-tertiary ") + (disabled === true ? "disabled" : "colour-primary-hover")}
                                onClick={() => (unitSwitch === false || disabled === true) ? null : unitSwitchFunc(true)}
                            >
                                {rightUnit}
                            </span>
                        }
                    </div>
                    {
                        prompt === true &&
                        <IconButton
                            size="x-small"
                            icon={<Arrow className="rotate-90 input-prompt-arrow" fill={colour.gray00} />}
                            disabled={submitPromptDisabled}
                            clickFunc={() => submitPrompt()}
                            standardIcon={false}
                            background={true}
                            bgType={"dark"}
                            contextTooltip={false}
                            contextTooltipBG={null}
                            contextTooltipText={null}
                            customClass={null}
                        />
                    }
                </div>
                {note}
                {
                    reset === true &&
                    <span
                        className="body-regular-underline colour-link input-reset-element"
                        onClick={() => resetFunc()}
                    >
                        Reset
                    </span>
                }
            </div>
        </div>
    )
};

export default Input;